import { useQuery } from "@tanstack/react-query";
import apiClient from "../client";

/** Mirrors Scr.Enums.OfferDiscountType. Sent as a number, so read it through this. */
export const OfferDiscountType = {
  Unknown: 0,
  Percent: 1,
  Amount: 2,
} as const;

/** Mirrors Scr.Enums.OfferBilling. */
export const OfferBilling = {
  Unknown: 0,
  Monthly: 1,
  Yearly: 2,
  Either: 3,
} as const;

export interface UsableOfferDto {
  id: string;
  publicName: string;
  /** The sentence written for customers. The offer reads from this, not from its terms. */
  customerWording: string;
  discountType: number;
  billing: number;
  /** A percentage, or a USD amount, according to discountType. */
  discountValue: number;
  /** Null runs until the discount is removed. */
  durationMonths: number | null;
  /** How many strategies it can be used on. Null is no limit. */
  maxStrategies: number | null;
  /** The last day it can be taken up, inclusive, UTC. */
  endsOn: string;
  /** The strategies it may be used on. Empty means any strategy. */
  tradingStrategyIds: string[];
}

export interface UsableOffersDto {
  offers: UsableOfferDto[];
}

const USABLE_OFFERS_KEY = ["offers", "usable"] as const;

export function useUsableOffers() {
  return useQuery<UsableOffersDto>({
    queryKey: USABLE_OFFERS_KEY,
    queryFn: async () => {
      const res = await apiClient.get<UsableOffersDto>("/offers/usable");
      return res.data;
    },
    // An offer is never the reason someone opened the portal, so a failure here leaves the rest of
    // the dashboard alone rather than retrying behind it. Same reasoning as partner offers.
    retry: false,
  });
}

/** Every offer the customer can take up today, or an empty list while loading or on failure. */
export function usableOffers(data: UsableOffersDto | undefined): UsableOfferDto[] {
  return data?.offers ?? [];
}

/**
 * How many the dashboard shows at once. Two, matching the partner offer slot: the dashboard should
 * not grow a column of offers as the catalogue does, and the offers page carries the rest.
 */
export const DASHBOARD_OFFER_SLOTS = 2;

/**
 * The offers that fit a cart line: limited to this strategy or to nothing, and billed the way the
 * line is.
 *
 * The billing half matters more than it looks. A "free month" is a month of a monthly subscription;
 * put on a yearly line it would read as a free year, which is neither what staff wrote nor what
 * Stripe would do with the coupon.
 */
export function offersForLine(
  offers: UsableOfferDto[],
  tradingStrategyId: string,
  billingCycle: "Monthly" | "Yearly"
): UsableOfferDto[] {
  const wanted = billingCycle === "Yearly" ? OfferBilling.Yearly : OfferBilling.Monthly;

  return offers.filter(
    (offer) =>
      (offer.billing === wanted || offer.billing === OfferBilling.Either) &&
      (offer.tradingStrategyIds.length === 0 ||
        offer.tradingStrategyIds.includes(tradingStrategyId))
  );
}
